import * as Toast from "@radix-ui/react-toast";
import {
  Trash,
  User2,
  Mail,
  Phone,
  Smartphone,
  StickyNote,
} from "lucide-react";
import { useState } from "react";
import { OwnerEditDialog } from "./OwnerEditDialog";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export function OwnerCard({
  id,
  name,
  telephone,
  cell_phone,
  email,
  observation,
  onOwnerUpdate,
}: {
  id: number;
  name: string;
  telephone: string;
  cell_phone: string;
  email: string;
  observation: string;
  onOwnerUpdate: () => void;
}) {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const route = import.meta.env.VITE_URL_ENDPOINT;

  async function handleDeleteOwner() {
    setLoading(true);
    try {
      const response = await fetch(`${route}/api/owners/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete owner");
      onOwnerUpdate();
      setToastType("success");
      setToastMessage("Proprietário excluído com sucesso!");
      setToastOpen(true);
    } catch (error) {
      setToastOpen(true);
      setToastType("error");
      setToastMessage("Falha ao excluir o proprietário!");
    } finally {
      setLoading(false);
      setOpenConfirm(false);
    }
  }

  return (
    <div className="w-full">
      <Toast.Provider swipeDirection="right">
        <div className="group mb-4 flex flex-col items-start justify-between rounded-xl border border-gray-100 bg-white p-6 shadow-md transition-all duration-200 hover:shadow-lg sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="mb-1 flex items-center gap-2">
              <User2 className="text-blue-600" size={20} />
              <h2 className="text-lg font-bold text-gray-900">{name}</h2>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Phone size={16} className="text-gray-400" />
                {telephone || (
                  <span className="italic text-gray-400">Sem telefone</span>
                )}
              </span>
              <span className="flex items-center gap-1">
                <Smartphone size={16} className="text-gray-400" />
                {cell_phone || (
                  <span className="italic text-gray-400">Sem celular</span>
                )}
              </span>
              <span className="flex items-center gap-1">
                <Mail size={16} className="text-gray-400" />
                {email || (
                  <span className="italic text-gray-400">Sem email</span>
                )}
              </span>
            </div>
            <p className="mt-2 flex items-center gap-1 text-sm text-gray-600">
              <StickyNote size={16} className="text-gray-400" />
              <span className="font-medium text-gray-500">Observação:</span>
              {observation || (
                <span className="italic text-gray-400">Sem observação</span>
              )}
            </p>
          </div>
          <div className="mt-4 flex items-center gap-2 sm:mt-0">
            <OwnerEditDialog
              id={id}
              currentName={name}
              currentTelephone={telephone}
              currentCellPhone={cell_phone}
              currentEmail={email}
              currentObservation={observation}
              onOwnerUpdate={onOwnerUpdate}
            />
            <Dialog open={openConfirm} onOpenChange={setOpenConfirm}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full bg-red-50 p-2 transition hover:bg-red-100"
                  onClick={() => setOpenConfirm(true)}
                  title="Excluir proprietário"
                >
                  <Trash className="text-red-600" />
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-xl bg-white p-4 shadow-lg sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">
                    Confirmar Exclusão
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-500">
                    Tem certeza que deseja excluir este proprietário? Esta ação
                    não pode ser desfeita.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    className="bg-red-600 text-white"
                    onClick={handleDeleteOwner}
                    disabled={loading}
                  >
                    {loading ? "Excluindo..." : "Excluir"}
                  </Button>
                  <Button
                    className="bg-gray-300"
                    onClick={() => setOpenConfirm(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          duration={5000}
          className={`fixed bottom-0 right-0 m-6 rounded-md p-4 shadow-md ${
            toastType === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <Toast.Description>{toastMessage}</Toast.Description>
          <Toast.Close className="absolute right-1 top-1" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
